import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Construction as ConstructionIcon,
  Code as CodeIcon,
  Rocket as RocketIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';

interface ComingSoonProps {
  title: string;
  description?: string;
  features?: string[];
  progress?: number;
}

const ComingSoon: React.FC<ComingSoonProps> = ({ 
  title, 
  description = "Cette fonctionnalité est actuellement en cours de développement.", 
  features = [],
  progress = 65
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ 
      p: 4, 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      minHeight: '60vh'
    }}>
      <Card sx={{ 
        maxWidth: 600, 
        width: '100%',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        border: `2px dashed ${theme.palette.primary.main}`,
        borderRadius: '20px',
        overflow: 'visible',
        position: 'relative',
      }}>
        {/* Animation de construction */}
        <Box sx={{
          position: 'absolute',
          top: -10,
          right: -10,
          width: 60,
          height: 60,
          backgroundColor: theme.palette.warning.main,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'bounce 2s infinite',
          boxShadow: `0 4px 20px ${theme.palette.warning.main}40`,
          '@keyframes bounce': {
            '0%, 20%, 50%, 80%, 100%': {
              transform: 'translateY(0)',
            },
            '40%': {
              transform: 'translateY(-10px)',
            },
            '60%': {
              transform: 'translateY(-5px)',
            },
          },
        }}>
          <ConstructionIcon sx={{ color: 'white', fontSize: 30 }} />
        </Box>

        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {/* Titre avec animation */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h3" sx={{ 
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
              animation: 'glow 2s ease-in-out infinite alternate',
              '@keyframes glow': {
                from: {
                  filter: 'brightness(1)',
                },
                to: {
                  filter: 'brightness(1.2)',
                },
              },
            }}>
              🚧 {title}
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              gap: 1,
              mb: 2
            }}>
              <CodeIcon sx={{ 
                color: theme.palette.primary.main,
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(1)',
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                  },
                },
              }} />
              <Typography variant="h6" color="text.secondary">
                En cours de développement
              </Typography>
              <CodeIcon sx={{ 
                color: theme.palette.secondary.main,
                animation: 'pulse 1.5s infinite 0.5s',
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(1)',
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                  },
                  '100%': {
                    transform: 'scale(1)',
                  },
                },
              }} />
            </Box>
          </Box>

          {/* Description */}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
            {description}
          </Typography>

          {/* Barre de progression animée */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimerIcon fontSize="small" />
                Progression du développement
              </Typography>
              <Typography variant="body2" color="primary.main" fontWeight="bold">
                {progress}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{ 
                height: 10, 
                borderRadius: 5,
                backgroundColor: theme.palette.grey[200],
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  animation: 'progressGlow 2s ease-in-out infinite alternate',
                  '@keyframes progressGlow': {
                    from: {
                      boxShadow: `0 0 5px ${theme.palette.primary.main}`,
                    },
                    to: {
                      boxShadow: `0 0 15px ${theme.palette.primary.main}`,
                    },
                  },
                }
              }} 
            />
          </Box>

          {/* Fonctionnalités à venir */}
          {features.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <RocketIcon color="primary" />
                Fonctionnalités à venir
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {features.map((feature, index) => (
                  <Chip
                    key={index}
                    label={feature}
                    variant="outlined"
                    color="primary"
                    sx={{
                      animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                      '@keyframes fadeInUp': {
                        from: {
                          opacity: 0,
                          transform: 'translateY(20px)',
                        },
                        to: {
                          opacity: 1,
                          transform: 'translateY(0)',
                        },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Points animés */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            gap: 1
          }}>
            <Typography variant="body2" color="text.secondary">
              Restez connecté pour les mises à jour
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    animation: `dot ${1.5}s infinite ${i * 0.5}s`,
                    '@keyframes dot': {
                      '0%, 80%, 100%': {
                        transform: 'scale(0)',
                        opacity: 0,
                      },
                      '40%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Effet de brillance */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            animation: 'shine 3s infinite',
            '@keyframes shine': {
              '0%': {
                left: '-100%',
              },
              '100%': {
                left: '100%',
              },
            },
          }} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ComingSoon;